import { faPercent } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogTitle, Divider, GridProps, InputAdornment, useTheme } from '@mui/material';
import { Checkbox, ContextForm, DatePicker, IFormAfterSubmit, InputOnlyField, Select, Text } from '@ngt/forms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext } from 'react';
import { Permissions, Project } from '../api/dtos';
import ProjectSummariesContext from '../contexts/ProjectSummariesContext';
import usePermissions from '../hooks/auth/usePermissions';
import { DollarInput, FixedHeightInputOnlyField, FormSection, IFormDialogProps, PercentInput, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent, StyledDivider, StyledFormColumn, StyledFormSubmitButton } from './inputs/CommonFormElements';

const COLUMN_SIZING: GridProps['size'] = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
    xl: 6
}

const ProjectForm: FC<IFormDialogProps> = (props) => {
    const {data: permissions} = usePermissions();
    const theme = useTheme();
    const { refresh: refreshProjectSummaries } = useContext(ProjectSummariesContext);

    const onAfterSubmit: IFormAfterSubmit<Project> = useCallback(async (_newValue, _formState, _formActions) => {
        await refreshProjectSummaries();
        props.onClose();
    }, [refreshProjectSummaries, props.onClose])

    return (
        <ContextForm
            onAfterSubmit={onAfterSubmit}
            validateOn={ValidateOn.onChange}
            validateOnReset
        >
            <StyledDialog {...props}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    {props.edit ? "Edit" : "Add"} Project
                </DialogTitle>
                <StyledDialogContent>
                    <FormSection>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Name"
                                component={Text}
                                propertyPath={'name'}
                            />
                            <InputOnlyField
                                label="Type" 
                                component={Select}
                                nullOption={false}
                                propertyPath={'type'}
                            />
                            <InputOnlyField
                                label="Status" 
                                component={Select}
                                nullOption={false}
                                propertyPath={'status'}
                            />
                            <PercentInput
                                label="Share" 
                                propertyPath={'percentShare'}
                            />
                            <InputOnlyField 
                                label="Default Back On Market (Days)"
                                component={Text}
                                propertyPath={'defaultBackOnMarket'}
                            />
                            <InputOnlyField
                                label="Default Settlement Adjustment (Days)" 
                                component={Text}
                                propertyPath={'defaultSettleDays'}
                            />
                            <InputOnlyField
                                label="Contract Report Date (Days)" 
                                component={Text}
                                propertyPath={'contractReportDateDays'}                    
                            />
                            <InputOnlyField
                                label="Contract Report Date Real Estate (Days)" 
                                component={Text}
                                propertyPath={'contractReportDateRealEstateDays'}                    
                            />
                        </StyledFormColumn>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Sage Project Code"
                                component={Text}
                                propertyPath={'sageProjectCode'}
                            />
                            <DollarInput 
                                label="Director's Valuation"
                                propertyPath={'valuation'}
                            />
                            <InputOnlyField 
                                label="Valuation Date"
                                component={DatePicker}
                                propertyPath={'valuationDate'}
                            />
                            <DollarInput 
                                label="Director's Valuation (Englobo)"
                                propertyPath={'valuationEnglobo'}
                            />
                            <InputOnlyField 
                                label="Valuation Date"
                                component={DatePicker}
                                propertyPath={'valuationDateEnglobo'}
                            />
                            <DollarInput
                                label="Director's Valuation (Per Lot)"
                                propertyPath="valuationPerLot"
                            />
                            <InputOnlyField 
                                label="Valuation Date"
                                component={DatePicker}
                                propertyPath={'valuationDatePerLot'}
                            />
                            <DollarInput 
                                label="Cost Per Lot"
                                propertyPath={'costPerLot'}
                            />
                        </StyledFormColumn>
                    </FormSection>
                    {
                        permissions?.includes(Permissions.ProjectCanEditFinanceAttributes) && 
                        <>
                            <StyledDivider />
                            <FormSection>
                                <StyledFormColumn
                                    size={COLUMN_SIZING}
                                >
                                    <InputOnlyField
                                        label="Landowner"
                                        component={Select}
                                        propertyPath={'landowner'}
                                    />
                                    <InputOnlyField
                                        label="Developer"
                                        component={Select}
                                        propertyPath={'developer'}
                                    />
                                    <InputOnlyField
                                        label="Development Director"
                                        component={Text}
                                        propertyPath={'developmentDirector'}
                                    />
                                    <InputOnlyField
                                        label="Project Manager"
                                        component={Text}
                                        propertyPath={'ProjectManager'}
                                    />
                                </StyledFormColumn>
                                <StyledFormColumn
                                    size={COLUMN_SIZING}
                                >
                                    <FixedHeightInputOnlyField
                                        label="Margin Scheme"
                                        component={Checkbox}
                                        propertyPath={'marginScheme'}
                                    />
                                    <InputOnlyField
                                        label="Developer Charge"
                                        component={Text}
                                        propertyPath={'developerCharge'}
                                        slotProps={{
                                            input: { // @TODO: Ask phil if this is a percentage
                                                endAdornment: <InputAdornment position="end"><FontAwesomeIcon icon={faPercent}/></InputAdornment>
                                            }
                                        }}
                                    />
                                    <InputOnlyField
                                        label="IA Commission"
                                        component={Text}
                                        propertyPath={'iaCommission'}
                                    />
                                    <DollarInput
                                        label="Landowner Per Lot Rate"
                                        propertyPath={'landownerPerLotRate'}
                                    />
                                </StyledFormColumn>
                            </FormSection>
                        </>
                    }
                </StyledDialogContent>
                <Divider />
                <StyledDialogActions>
                    <StyledFormSubmitButton variant="contained">Submit</StyledFormSubmitButton>
                    <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
        </ContextForm>
    );
}

export default ProjectForm;